import React, { useState, useEffect, useCallback } from 'react';
import { filter } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  Table,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  tableCellClasses
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead } from '../../components/_dashboard/user';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import usePayoutSelector from '../../store/Slice/Payout/PayoutSelector';
import { getPLList } from '../../store/Slice/Payout/PayoutSlice';
import useDebounce from '../../utils/useDebounce';
import NetworkToolbar from './NetworkToolbar';
import { normalizeValue } from '../../utils/CommonFunction';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'referenceId', label: 'Reference ID', alignRight: false },
  { id: 'fileNumber', label: 'File Number', alignRight: false },
  { id: 'date', label: 'Date', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'income', label: 'Income', alignRight: false },
  { id: 'expense', label: 'Expense', alignRight: false },
  { id: 'balance', label: 'Balance', alignRight: false },
  { id: 'tds', label: 'TDS', alignRight: false },
  { id: 'gst', label: 'GST', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'remarks', label: 'Remarks', alignRight: false }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        (_user.referenceId &&
          String(_user.referenceId).toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user.fileNumber &&
          String(_user.fileNumber).toLowerCase().indexOf(query.toLowerCase()) !== -1) ||
        (_user.description && _user.description.toLowerCase().indexOf(query.toLowerCase()) !== -1)
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 13,
    fontWeight: 'bold',
    padding: '8px 12px',
    backgroundColor: '#ffffff',
    color: '#13519d'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '8px 12px'
  }
}));

export default function PandL() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage] = useState(10000);

  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { PayoutPLData } = usePayoutSelector();
  const [pageMenu] = useState([]);
  const [filterState, setFilterState] = useState({
    typeId: null,
    isActive: null,
    cityId: null
  });

  const getPLListData = useCallback(() => {
    if (!LoginData?.userId) return;
    const credential = {
      userId: LoginData?.userId
    };
    dispatch(getPLList(credential));
  }, [LoginData, dispatch]);

  useEffect(() => {
    getPLListData();
  }, [getPLListData]);

  const PandLDataList = PayoutPLData?.summary?.transactions || [];

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = PandLDataList.map((n) => n.referenceId || n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const filteredUsers = applySortFilter(PandLDataList, getComparator(order, orderBy), filterName);
  const isUserNotFound = filteredUsers.length === 0;

  return (
    <Box sx={{ width: '100%' }}>
      <NetworkToolbar
        numSelected={selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
        parent="payout"
        pageMenu={pageMenu}
        filterState={filterState}
        setFilterState={setFilterState}
        setFilterName={setFilterName}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 800, overflow: 'auto' }}>
          <Table>
            <UserListHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={PandLDataList.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
              isCheckBox="true"
            />
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const {
                    referenceId,
                    fileNumber,
                    date,
                    description,
                    income,
                    expense,
                    balance,
                    tds,
                    gst,
                    status,
                    remarks
                  } = row;
                  const itemKey = referenceId || index;
                  const isItemSelected = selected.indexOf(referenceId) !== -1;

                  return (
                    <TableRow
                      hover
                      key={itemKey}
                      tabIndex={-1}
                      role="checkbox"
                      selected={isItemSelected}
                      aria-checked={isItemSelected}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: 'rgba(19, 81, 157, 0.08)'
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, referenceId)}
                          sx={{
                            color: '#94a3b8',
                            '&.Mui-checked': { color: '#13519d' }
                          }}
                        />
                      </TableCell>

                      <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#13519d' }}>
                        {referenceId || '—'}
                      </StyledTableCell>
                      <StyledTableCell align="left" sx={{ fontWeight: 600, color: '#13519d' }}>
                        {fileNumber || '—'}
                      </StyledTableCell>
                      <StyledTableCell align="left">{date || '—'}</StyledTableCell>
                      <StyledTableCell align="left">{description || '—'}</StyledTableCell>
                      <StyledTableCell align="left">
                        {income ? (
                          <Label variant="ghost" color="success">
                            {income}
                          </Label>
                        ) : (
                          '—'
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        {expense ? (
                          <Label variant="ghost" color="error">
                            {expense}
                          </Label>
                        ) : (
                          '—'
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="left" sx={{ fontWeight: 700 }}>
                        {normalizeValue(balance, '—')}
                      </StyledTableCell>
                      <StyledTableCell align="left">{normalizeValue(tds, '—')}</StyledTableCell>
                      <StyledTableCell align="left">{normalizeValue(gst, '—')}</StyledTableCell>
                      <StyledTableCell align="left">
                        {status ? (
                          <Label
                            variant="ghost"
                            color={
                              status === 'Completed' || status === 'Approved' || status === 'Paid'
                                ? 'success'
                                : status === 'Pending'
                                ? 'warning'
                                : 'default'
                            }
                          >
                            {status}
                          </Label>
                        ) : (
                          '—'
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="left">{remarks || '—'}</StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            {isUserNotFound && (
              <TableBody>
                <TableRow>
                  <StyledTableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 3 }}>
                    <SearchNotFound searchQuery={filterName} />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
    </Box>
  );
}
